import React, { useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminBroadcast, type Severity } from '../../../api/notificationsApi';
import { S } from './adminPageStyles';

/** Announcements (SRS #100/#101): release notes to everyone, or an upgrade campaign to selected plans. */

const TIERS = ['academic', 'startup', 'professional', 'law_firm_individual', 'business', 'law_firm_team', 'growth', 'government_state', 'government_federal', 'enterprise'];

const AdminAnnouncementsPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [severity, setSeverity] = useState<Severity>('info');
  const [tiers, setTiers] = useState<string[]>([]);
  const [email, setEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const who = tiers.length ? `users on ${tiers.join(', ')}` : 'every active user';
    if (!window.confirm(`Send "${title}" to ${who}${email ? ' — and email them' : ''}?`)) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await adminBroadcast({ title, body: body || undefined, link: link || undefined, severity, plan_tiers: tiers.length ? tiers : undefined, email });
      setResult(`Delivered to ${r.recipients} user${r.recipients === 1 ? '' : 's'}.`);
      setTitle(''); setBody(''); setLink('');
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to send'); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Announcements" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Announcements</h2>
        <p style={S.sub}>Appears in every recipient's notification centre (bell). Use it for weekly release notes, maintenance windows, and upgrade campaigns targeted at the plans that lack a feature. Email is off by default.</p>
        {error && <div style={S.error}>{error}</div>}
        {result && <div style={S.ok}>{result}</div>}
        <form onSubmit={send} style={{ display: 'grid', gap: 12, maxWidth: 680 }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Title
            <input style={S.input} value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={200} placeholder="New this week: side-by-side comparison matrix" />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Message
            <textarea style={{ ...S.input, minHeight: 90, font: 'inherit' }} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} placeholder="What changed and why it matters." />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Link (in-app path such as /comparison, or https://…)
            <input style={S.input} value={link} onChange={(e) => setLink(e.target.value)} maxLength={300} placeholder="/comparison" />
          </label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
            <label>Severity{' '}
              <select style={S.input} value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
                <option value="info">Info (bell only)</option>
                <option value="warning">Warning (banner)</option>
                <option value="critical">Critical (red banner)</option>
              </select>
            </label>
            <label><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Also send by email</label>
          </div>
          <fieldset style={{ border: '1px solid var(--ds-border)', borderRadius: 8, padding: '8px 12px' }}>
            <legend style={{ fontSize: 12.5, color: 'var(--ds-text-muted)' }}>Plans (none selected = everyone)</legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 4 }}>
              {TIERS.map((t) => (
                <label key={t} style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={tiers.includes(t)} onChange={(e) => setTiers((xs) => (e.target.checked ? [...xs, t] : xs.filter((x) => x !== t)))} /> {t.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
          </fieldset>
          <div><button type="submit" style={S.primary} disabled={busy || title.trim().length < 3}>{busy ? 'Sending…' : 'Send announcement'}</button></div>
        </form>
        <h3 style={{ ...S.h2, fontSize: '1rem', marginTop: 28 }}>What goes out automatically</h3>
        <ul style={{ fontSize: 13, color: 'var(--ds-text-secondary)', lineHeight: 1.7 }}>
          <li><strong>Renewal reminders</strong> — 7, 3 and 1 days before a renewal or plan end (daily job); payment failed; expired; renewed.</li>
          <li><strong>Trial ending</strong> — 3 and 1 days before, in-app (the trial email sequence continues separately).</li>
          <li><strong>Upgrade suggestions</strong> — when a user hits a plan limit (reports, team collaboration, seats, attachments); once per feature per day; in-app only.</li>
          <li><strong>Plan activated / changed</strong> — after checkout.</li>
          <li><strong>Support</strong> replies and status changes; <strong>team</strong> invitations; <strong>alert</strong> matches; <strong>new device</strong> sign-ins.</li>
        </ul>
      </section>
    </DashboardLayout>
  );
};

export default AdminAnnouncementsPage;
